import { isEmpty } from 'ramda';
import { createSelector } from 'reselect';

import { BRAND_TYPES } from 'constants/BrandConstants';
import type { Brand } from 'modules/brands/types';
import type { RootState } from 'reducers/index';
import { filter } from 'utils/global';
import { pickByIds } from 'utils/ObjectUtils';

import type { BrandsSlice } from './types';

const getState = (state: RootState) => state.brands;

const getUiState = createSelector(getState, state => state.ui);

const getEntities = createSelector(getState, state => state?.entities);

// get all brand whether or not they have templates
export const getRentalVehicleBrands = createSelector(getEntities, entities => {
  const { byId } = entities;
  return Object.values(byId)
    .filter(({ type }) => type === BRAND_TYPES.OFFICIAL)
    .sort(({ name: nameA }, { name: nameB }) => nameA.localeCompare(nameB));
});

const getDealerId = (_: BrandsSlice, dealerId: string) => dealerId;

const getGroupId = (_: BrandsSlice, groupId: string) => groupId;

const getBrandId = (_: BrandsSlice, brandId: string) => brandId;

const getIds = (_: BrandsSlice, ids: string[]) => ids;

export const getAllBrands = createSelector(getEntities, entities =>
  filter(({ templated }) => templated, entities.byId) as Record<string, Brand>);

export const getAllBrandsIds = createSelector(getAllBrands, brands => Object.keys(brands));

export const getBrandIdsByDealerId = createSelector(getEntities, entities => entities?.byDealerId ?? {});

export const getBrandIdsByGroupId = createSelector(getEntities, entities => entities?.byGroupId ?? {});

export const getBrandIdsCollectionByDealerId = createSelector(
  getBrandIdsByDealerId,
  getDealerId,
  (entries, dealerId) => entries[dealerId] ?? []
);

export const getBrandsByDealerId = createSelector(
  getBrandIdsByDealerId,
  getAllBrands,
  getDealerId,
  (dealerBrandIds, brands, dealerId) => {
    if (!isEmpty(dealerBrandIds) && !isEmpty(brands)) {
      const brandIds = dealerBrandIds[dealerId] || [];
      return pickByIds(brandIds, brands);
    }
    return {} as Record<string, Brand>;
  }
);

export const getBrandsListByDealerId = createSelector(
  getBrandsByDealerId,
  brands => Object.values(brands)
);

export const getBrandsByGroupId = createSelector(
  getBrandIdsByGroupId,
  getAllBrands,
  getGroupId,
  (groupBrandIds, brands, groupId) => {
    if (!isEmpty(groupBrandIds) && !isEmpty(brands)) {
      const brandIds = groupBrandIds[groupId] || [];
      return pickByIds(brandIds, brands);
    }
    return {} as Record<string, Brand>;
  }
);

export const getBrandsIsFetching = createSelector(getUiState, ui => ui.fetching);

export const getBrandsApiError = createSelector(getUiState, ui => ui.apiError);

export const getBrandById = createSelector(getAllBrands, getBrandId, (brands, id) => brands?.[id]);

export const getBrandByIds = createSelector(getAllBrands, getIds, (brands, ids) =>
  ids.reduce<Brand[]>((acc, brandId) => {
    const brand = brands?.[brandId];
    if (brand) {
      acc.push(brand);
    }
    return acc;
  }, []));
